import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useStaticQuery, graphql } from "gatsby";
import { ThemeProvider } from "styled-components";
import styled from "styled-components";
import CookieConsent, { Cookies } from "react-cookie-consent";
import { GlobalStyle } from "../style/global.js";
import GoogleTagManager from "./google-tagmanager.js";
import Header from "./header.js";
import Footer from "./footer.js";
import USP from "./usp.js";
import IconLibrary from "./icon-library";
import PromoBanner from "./promobanner.js";
import SyncCart from "./sync-cart";
import { _ } from "../utils/i18n";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

const Wrapper = styled.main`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 0 auto;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
`;

export const ConsentContext = React.createContext(null);

export default function Layout({
  children,
  staticQueryData,
  location,
  isFrontPage,
}) {
  const { wp } =
    staticQueryData ||
    useStaticQuery(graphql`
      query LayoutQuery {
        wp {
          crbThemeOptions {
            crbLineHeight
            crbLetterSpacing
            crbPaddingBottom
            crbPaddingLeft
            crbPaddingRight
            crbPaddingTop
            crbPromoEnabled
            crbPromoText
            crbBorderRadius
            crbBorderRadiusBottomLeft
            crbBorderRadiusBottomRight
            crbBorderRadiusTopLeft
            crbBorderRadiusTopRight
            crbSameBorderRadius {
              value
            }
            crbBorderRadiusContainerBlocks
            crbBorderRadiusContainerBlocksBottomLeft
            crbBorderRadiusContainerBlocksBottomRight
            crbBorderRadiusContainerBlocksTopLeft
            crbBorderRadiusContainerBlocksTopRight
            crbSameBorderRadiusContainerBlocks {
              value
            }
            crbUsp {
              icon {
                json
              }
              text
              url
            }
            crbUspAdditional {
              icon {
                json
              }
              text
              url
            }
            crbColorUspBackground
            crbColorUspBorderBottom
            crbColorUspIcon
            crbColorUspText
            crbColorAccent1
            crbColorAccent2
            crbColorAccent3
            crbColorBlack
            crbColorPrimary
            crbColorSecondary
            crbColorWhite
            crbColorHeaderBackground
            crbColorTransparentHeader
            crbColorHeaderLink
            crbColorHeaderButton
            crbColorHeaderButtonText
            crbColorHeaderSearch
            crbColorFooterBackgroundTop
            crbColorFooterBackgroundBottom
            crbColorFooterTextTop
            crbColorFooterTextBottom
            crbColorFooterButton
            crbColorFooterButtonText
            crbColorBody
            crbColorBodyText
            crbColorBodyLink
            crbColorPromobannerBackground
            crbColorPromobannerIcon
            crbColorPromobannerLinkColor
            crbColorPromobannerText
            crbFontHeadingsWoff {
              title
              localFile {
                publicURL
              }
            }
            crbFontHeadingsWoff2 {
              title
              localFile {
                publicURL
              }
            }
            crbFontLinksWoff {
              title
              localFile {
                publicURL
              }
            }
            crbFontLinksWoff2 {
              title
              localFile {
                publicURL
              }
            }
            crbFontParagraphWoff {
              title
              localFile {
                publicURL
              }
            }
            crbFontParagraphWoff2 {
              title
              localFile {
                publicURL
              }
            }
            crbFontButtonsWoff {
              title
              localFile {
                publicURL
              }
            }
            crbFontButtonsWoff2 {
              title
              localFile {
                publicURL
              }
            }
            crbExtraFeatureWoocommerce
            crbSecretKey
            crbSiteKey
          }
          customCSS
        }
      }
    `);
  const [buttonBorderRadius] = React.useState(() => {
    if (wp.crbThemeOptions.crbSameBorderRadius.value === "yes") {
      return `${wp.crbThemeOptions.crbBorderRadius || 0}px`;
    } else {
      return `${wp.crbThemeOptions.crbBorderRadiusTopLeft}px ${wp.crbThemeOptions.crbBorderRadiusTopRight}px ${wp.crbThemeOptions.crbBorderRadiusBottomRight}px ${wp.crbThemeOptions.crbBorderRadiusBottomLeft}px`;
    }
  });

  const [containerBlocksBorderRadius] = React.useState(() => {
    if (wp.crbThemeOptions.crbSameBorderRadiusContainerBlocks.value === "yes") {
      return `${wp.crbThemeOptions.crbBorderRadiusContainerBlocks || 0}px`;
    } else {
      return `${wp.crbThemeOptions.crbBorderRadiusContainerBlocksTopLeft}px ${wp.crbThemeOptions.crbBorderRadiusContainerBlocksTopRight}px ${wp.crbThemeOptions.crbBorderRadiusContainerBlocksBottomRight}px ${wp.crbThemeOptions.crbBorderRadiusContainerBlocksBottomLeft}px`;
    }
  });

  const theme = React.useMemo(() => {
    return {
      name: "Default Theme from WP",
      colors: {
        primary: `${wp.crbThemeOptions.crbColorPrimary}`,
        secondary: `${wp.crbThemeOptions.crbColorSecondary}`,
        black: `${wp.crbThemeOptions.crbColorBlack}`,
        white: `${wp.crbThemeOptions.crbColorWhite}`,
        accent1: `${wp.crbThemeOptions.crbColorAccent1}`,
        accent2: `${wp.crbThemeOptions.crbColorAccent2}`,
        accent3: `${wp.crbThemeOptions.crbColorAccent3}`,
        body: `${wp.crbThemeOptions.crbColorBody}`,
        bodyText: `${wp.crbThemeOptions.crbColorBodyText}`,
        bodyLink: `${wp.crbThemeOptions.crbColorBodyLink}`,
        darkGray: "#333",
        lightGray: "#6e6e6e",
        headerBackground: `${wp.crbThemeOptions.crbColorHeaderBackground}`,
        transparentHeaderTextColor: `${wp.crbThemeOptions.crbColorTransparentHeader}`,
        headerLink: `${wp.crbThemeOptions.crbColorHeaderLink}`,
        headerButton: `${wp.crbThemeOptions.crbColorHeaderButton}`,
        headerButtonText: `${wp.crbThemeOptions.crbColorHeaderButtonText}`,
        headerSearch: `${wp.crbThemeOptions.crbColorHeaderSearch}`,
        footerBackgroundTop: `${wp.crbThemeOptions.crbColorFooterBackgroundTop}`,
        footerTextTop: `${wp.crbThemeOptions.crbColorFooterTextTop}`,
        footerBackgroundBottom: `${wp.crbThemeOptions.crbColorFooterBackgroundBottom}`,
        footerTextBottom: `${wp.crbThemeOptions.crbColorFooterTextBottom}`,
        footerButton: `${wp.crbThemeOptions.crbColorFooterButton}`,
        footerButtonText: `${wp.crbThemeOptions.crbColorFooterButtonText}`,
        uspBackground: `${wp.crbThemeOptions.crbColorUspBackground}`,
        uspBorderBottom: `${wp.crbThemeOptions.crbColorUspBorderBottom}`,
        uspText: `${wp.crbThemeOptions.crbColorUspText}`,
        uspIcon: `${wp.crbThemeOptions.crbColorUspIcon}`,
        promobannerBackground: `${wp.crbThemeOptions.crbColorPromobannerBackground}`,
        promobannerLinkColor: `${wp.crbThemeOptions.crbColorPromobannerLinkColor}`,
        promobannerText: `${wp.crbThemeOptions.crbColorPromobannerText}`,
        promobannerIcon: `${wp.crbThemeOptions.crbColorPromobannerIcon}`,
      },
      fontHeadings: {
        woff: `${wp.crbThemeOptions.crbFontHeadingsWoff?.localFile?.publicURL}`,
        woff2: `${wp.crbThemeOptions.crbFontHeadingsWoff2?.localFile?.publicURL}`,
        fontFamily: `${
          wp.crbThemeOptions.crbFontHeadingsWoff?.localFile?.publicURL &&
          wp.crbThemeOptions.crbFontHeadingsWoff2?.localFile?.publicURL
            ? wp.crbThemeOptions.crbFontHeadingsWoff?.title
            : "Arial, sans-serif"
        }`,
      },
      fontLinks: {
        woff: `${wp.crbThemeOptions.crbFontLinksWoff?.localFile?.publicURL}`,
        woff2: `${wp.crbThemeOptions.crbFontLinksWoff2?.localFile?.publicURL}`,
        fontFamily: `${
          wp.crbThemeOptions.crbFontLinksWoff?.localFile?.publicURL &&
          wp.crbThemeOptions.crbFontLinksWoff2?.localFile?.publicURL
            ? wp.crbThemeOptions.crbFontLinksWoff?.title
            : "Arial, sans-serif"
        }`,
      },
      fontParagraphs: {
        woff: `${wp.crbThemeOptions.crbFontParagraphWoff?.localFile?.publicURL}`,
        woff2: `${wp.crbThemeOptions.crbFontParagraphWoff2?.localFile?.publicURL}`,
        fontFamily: `${
          wp.crbThemeOptions.crbFontParagraphWoff?.localFile?.publicURL &&
          wp.crbThemeOptions.crbFontParagraphWoff2?.localFile?.publicURL
            ? wp.crbThemeOptions.crbFontParagraphWoff?.title
            : "Arial, sans-serif"
        }`,
      },
      fontButtons: {
        woff: `${wp.crbThemeOptions.crbFontButtonsWoff?.localFile?.publicURL}`,
        woff2: `${wp.crbThemeOptions.crbFontButtonsWoff2?.localFile?.publicURL}`,
        fontFamily: `${
          wp.crbThemeOptions.crbFontButtonsWoff?.localFile?.publicURL &&
          wp.crbThemeOptions.crbFontButtonsWoff2?.localFile?.publicURL
            ? wp.crbThemeOptions.crbFontButtonsWoff?.title
            : "Arial, sans-serif"
        }`,
      },
      typography: {
        fontFamily: "Arial, sans-serif",
      },
      breakpoints: {
        wide: "1180px",
        wideTablet: "1025px",
        tablet: "850px",
        phone: "600px",
      },
      generalLineHeight: wp.crbThemeOptions.crbLineHeight,
      generalLetterSpacing: wp.crbThemeOptions.crbLetterSpacing,
      borderRadius: buttonBorderRadius ? buttonBorderRadius : "0px",
      containerBlocksBorderRadius: containerBlocksBorderRadius
        ? containerBlocksBorderRadius
        : "0px",
      customCSS: wp.customCSS,
    };
  }, [wp.crbThemeOptions, buttonBorderRadius, containerBlocksBorderRadius]);

  const [consent, setConsent] = React.useState({
    googleMaps: undefined,
    googleTagManager: undefined,
    instagram: undefined,
  });

  const [headerContentVisibility, setHeaderContentVisibility] =
    React.useState(true);

  const [lastScrollY, setLastScrollY] = React.useState(true);

  const controlHeaderContent = () => {
    if (lastScrollY == true) {
      setHeaderContentVisibility(true);
    } else if (window.scrollY - 50 > lastScrollY) {
      setHeaderContentVisibility(false);
    } else if (window.scrollY + 50 < lastScrollY) {
      setHeaderContentVisibility(true);
    }

    setLastScrollY(window.scrollY);
  };

  const toKebabCase = (str) => str.replace(/[A-Z]/g, "-$&").toLowerCase();
  const toCamelCase = (str) => str.replace(/-./g, (x) => x[1].toUpperCase());

  const updateConsent = (values) => {
    if (process.env.GATSBY_CONSENT_SCRIPT_URL) {
      const eventDetail = Object.keys(values).reduce((choices, key) => {
        choices[toKebabCase(key)] = values[key] ? "granted" : "denied";
        return choices;
      }, {});

      document.dispatchEvent(
        new CustomEvent("update-mev-consent", { detail: eventDetail })
      );
    } else {
      Object.keys(values).forEach((key) => {
        Cookies.set(`consent_${key}`, values[key].toString(), { expires: 365 });
      });
    }

    setConsent({ ...consent, ...values });
  };

  const handleMevConsent = (event) => {
    const newConsent = Object.keys(event.detail).reduce((choices, key) => {
      choices[toCamelCase(key)] = event.detail[key] === "granted";
      return choices;
    }, {});

    setConsent({ ...consent, ...newConsent });
  };

  useEffect(() => {
    const mevConsentListener = (event) => handleMevConsent(event);

    if (process.env.GATSBY_CONSENT_SCRIPT_URL) {
      document.addEventListener("mev-consent", mevConsentListener);
    }

    const consentCookieValues = Object.keys(consent).reduce((values, key) => {
      values[key] = Cookies.get(`consent_${key}`) === "true";
      return values;
    }, {});

    setConsent({ ...consent, ...consentCookieValues });

    return () => {
      document.removeEventListener("mev-consent", mevConsentListener);
    };
  }, []);

  useEffect(() => {
    if (lastScrollY == true) {
      controlHeaderContent;
    }

    window.addEventListener("scroll", controlHeaderContent);
    return () => window.removeEventListener("scroll", controlHeaderContent);
  }, [lastScrollY]);

  return (
    <ConsentContext.Provider value={{ consent, updateConsent }}>
      <GoogleReCaptchaProvider
        scriptProps={{
          defer: true,
        }}
        reCaptchaKey={wp.crbThemeOptions.crbSiteKey}
      >
        {!process.env.GATSBY_CONSENT_SCRIPT_URL && (
          <>
            <CookieConsent
              buttonText={_("Jeg samtykker")}
              declineButtonText={_("Jeg samtykker ikke")}
              declineButtonClasses="button secondary"
              buttonClasses="button"
              cookieName="consentBanner_v2"
              enableDeclineButton
              disableStyles
              onAccept={() =>
                updateConsent({
                  googleMaps: true,
                  googleTagManager: true,
                })
              }
              onDecline={() =>
                updateConsent({
                  googleMaps: false,
                  googleTagManager: false,
                })
              }
            >
              <p>
                {_(
                  "Dette nettstedet trenger informasjonskapsler og tredjepartsskript for full funksjonalitet og analyse. For mer informasjon, les vår"
                )}{" "}
                <Link to="/personvern/" style={{ color: "white" }}>
                  {_("personvernpolicy")}
                </Link>
                .
              </p>
            </CookieConsent>
            <GoogleTagManager />
          </>
        )}
        <ThemeProvider theme={theme}>
          <IconLibrary />
          <Header
            isFrontPage={isFrontPage}
            staticQueryData={staticQueryData}
            location={location}
            staticSearchData={staticQueryData}
            showUsp={
              wp?.crbThemeOptions?.crbUsp.length > 0 ||
              wp?.crbThemeOptions?.crbUspAdditional.length > 0
            }
          >
            <PromoBanner
              promoEnabled={wp.crbThemeOptions.crbPromoEnabled}
              promoText={wp.crbThemeOptions.crbPromoText}
              scrollDisable={headerContentVisibility}
            />
            <USP
              list={wp?.crbThemeOptions?.crbUsp}
              additional={wp?.crbThemeOptions?.crbUspAdditional}
              paddingTop={wp?.crbThemeOptions?.crbPaddingTop}
              paddingBottom={wp?.crbThemeOptions?.crbPaddingBottom}
              paddingRight={wp?.crbThemeOptions?.crbPaddingRight}
              paddingLeft={wp?.crbThemeOptions?.crbPaddingLeft}
              scrollDisable={headerContentVisibility}
            />
          </Header>
          <GlobalStyle />
          <Wrapper>{children}</Wrapper>
          <Footer staticQueryData={staticQueryData} />
          {wp?.crbThemeOptions?.crbExtraFeatureWoocommerce && <SyncCart />}
        </ThemeProvider>
      </GoogleReCaptchaProvider>
    </ConsentContext.Provider>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
  staticQueryData: PropTypes.object,
  location: PropTypes.object,
  isFrontPage: PropTypes.bool,
};

Layout.defaultProps = {
  children: undefined,
  staticQueryData: undefined,
  location: undefined,
};
